// noinspection SpellCheckingInspection

import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { WorkAgreementOrderField } from "work/work-agreements/_api/queries/_fields/work-agreement-order-field";
import WorkAgreementQuerySchema, {
  WorkAgreementFilterParametersJSON,
  WorkAgreementFilterParametersParams,
  WorkAgreementFilterParametersSchema,
  WorkAgreementOrderParametersParams,
  WorkAgreementOrderParametersSchema,
  WorkAgreementQueryJSON,
  WorkAgreementQueryParams,
} from "work/work-agreements/_api/queries/_schemas/work-agreement-query-schema";

export default class WorkAgreementQuery {
  public static create(params: WorkAgreementQueryParams): WorkAgreementQuery {
    const parsedParams = WorkAgreementQuerySchema.validate(params);

    return new WorkAgreementQuery({
      filterParameters: parsedParams.filterParameters ?? WorkAgreementFilterParameters.create({}),
      orderParameters:
        parsedParams.orderParameters ??
        WorkAgreementOrderParameters.create({
          orderBy: WorkAgreementOrderField.Start,
          orderDirection: OrderDirection.Descending,
        }),
      paginationParameters: parsedParams.paginationParameters,
    });
  }

  public static fromJSON(json: unknown): WorkAgreementQuery | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsedParams = WorkAgreementQuerySchema.deserialize(json);
    if (!parsedParams) return parsedParams;
    return new WorkAgreementQuery(parsedParams);
  }

  public filterParameters: WorkAgreementFilterParameters;
  public orderParameters: WorkAgreementOrderParameters;
  public paginationParameters: PaginationParameters;

  protected constructor(params: {
    filterParameters?: WorkAgreementFilterParameters;
    orderParameters?: WorkAgreementOrderParameters;
    paginationParameters?: PaginationParameters;
  }) {
    this.filterParameters = params.filterParameters ?? WorkAgreementFilterParameters.create({});
    this.orderParameters =
      params.orderParameters ??
      WorkAgreementOrderParameters.create({
        orderBy: WorkAgreementOrderField.Start,
        orderDirection: OrderDirection.Descending,
      });
    this.paginationParameters = params.paginationParameters ?? PaginationParameters.DEFAULT;
  }

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.filterParameters.searchParams,
      ...this.orderParameters.searchParams,
      ...this.paginationParameters.searchParams,
    ]);
  }

  public get cacheKey(): string {
    const params = this.searchParams;
    const sortedEntries = [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries.map(([k, v]) => `${k}=${v}`).join("&");
  }

  public toJSON(): WorkAgreementQueryJSON {
    return WorkAgreementQuerySchema.serialize(this);
  }

    public isEqualTo(other: WorkAgreementQuery): boolean {
    if (!other) return false;
    return (
      this.filterParameters.isEqualTo(other.filterParameters) &&
      this.orderParameters.isEqualTo(other.orderParameters) &&
      this.paginationParameters.isEqualTo(other.paginationParameters)
    );
  }
}

export class WorkAgreementFilterParameters {
  public static create(params: WorkAgreementFilterParametersParams): WorkAgreementFilterParameters {
    const parsed = WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ.parse(params);
    return new WorkAgreementFilterParameters(parsed);
  }

  public static fromJSON(
    json: WorkAgreementFilterParametersJSON | null | undefined
  ): WorkAgreementFilterParameters | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ.parse(json);
    return new WorkAgreementFilterParameters(parsed);
  }

  public static fromSearchParams(args: {
    searchParams: URLSearchParams;
    options?: {
      queryParamName: string;
      nameParamName: string;
      descriptionParamName: string;
    };
    overrides?: Partial<WorkAgreementFilterParametersParams>;
  }): WorkAgreementFilterParameters {
    const { searchParams, overrides } = args;
    let { options } = args;
    options = options ?? {
      queryParamName: "query",
      nameParamName: "name",
      descriptionParamName: "description",
    };

    const params = WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ.parse({
      ...Object.fromEntries(searchParams),
      query: searchParams.get(options.queryParamName) ?? undefined,
      name: searchParams.get(options.nameParamName) ?? undefined,
      description: searchParams.get(options.descriptionParamName) ?? undefined,
      // Add additional parameters as needed
    });
    return new WorkAgreementFilterParameters({
      ...params,
      ...overrides,
    });
  }
  
  public readonly query?: string;
  public readonly RFPId?: Guid;
  public readonly name?: string;
  public readonly description?: string;
  public readonly clientUserId?: Guid;
  public readonly clientEntityId?: Guid;
  public readonly clientTeamMemberUserId?: Guid;
  public readonly clientTeamMemberEntityId?: Guid;
  public readonly minStart?: AHDate;
  public readonly maxStart?: AHDate;
  public readonly hasEnd?: boolean;
  public readonly minEnd?: AHDate;
  public readonly maxEnd?: AHDate;
  public readonly vendorTeamRestricted?: boolean;
  public readonly vendorUserId?: Guid;
  public readonly vendorEntityId?: Guid;
  public readonly vendorTeamMemberUserId?: Guid;
  public readonly vendorTeamMemberEntityId?: Guid;
  public readonly additionalVendorUserId?: Guid;
  public readonly additionalVendorEntityId?: Guid;
  public readonly feeScheduleCategoryId?: Guid;
  public readonly feeScheduleCategoryName?: string;
  public readonly feeScheduleCategoryDescription?: string;
  public readonly teamTemplateId?: Guid;
  public readonly teamTemplateName?: string;
  public readonly feeScheduleTemplateId?: Guid;
  public readonly feeScheduleTemplateName?: string;
  public readonly minDiscount?: number;
  public readonly maxDiscount?: number;
  public readonly isArchived?: boolean;
  public readonly maxArchived?: AHDate;
  public readonly minArchived?: AHDate;
  public readonly userId?: Guid;
  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(this)) {
      if (value instanceof AHDate) {
        params.append(key, value.toISOString());
      } else if (value !== undefined) {
        params.append(key, value?.toString() ?? "null");
      }
    }
    return params;
  }

  public toJSON(): WorkAgreementFilterParametersJSON {
    return WorkAgreementFilterParametersSchema.serialize(this);
  }

  isEqualTo(other: WorkAgreementFilterParameters): boolean {
    return Object.entries(this).every(([key, value]) => {
      const otherValue = other[key as keyof WorkAgreementFilterParameters];
      if (value instanceof Guid && otherValue instanceof Guid) {
        return value.isEqualTo(otherValue);
      }
      if (value instanceof AHDate && otherValue instanceof AHDate) {
        return value.isEqualTo(otherValue);
      }
      return value === otherValue;
    });
  }

  public constructor(params: WorkAgreementFilterParametersParams) {
    Object.assign(this, params);
  }
}

export class WorkAgreementOrderParameters {
  public static create(params: WorkAgreementOrderParametersParams): WorkAgreementOrderParameters {
    if (!params.orderBy) {
      params.orderBy = WorkAgreementOrderField.Start;
    }
    if (!params.orderDirection) {
      params.orderDirection = OrderDirection.Descending;
    }
    const parsed = WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersZ.parse(params);
    return new WorkAgreementOrderParameters(parsed);
  }

  public static fromJSON(json: unknown): WorkAgreementOrderParameters | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersZ.parse(json);
    return new WorkAgreementOrderParameters(parsed);
  }

  public static fromSearchParams(
    searchParams: URLSearchParams,
    options?: {
      orderByParamName: string;
      orderDirParamName: string;
      defaultOrderBy: WorkAgreementOrderField;
      defaultOrderDir: OrderDirection;
    }
  ): WorkAgreementOrderParameters {
    options ??= {
      orderByParamName: "orderBy",
      orderDirParamName: "orderDir",
      defaultOrderBy: WorkAgreementOrderField.Start,
      defaultOrderDir: OrderDirection.Descending,
    };
    let { orderByParamName, orderDirParamName, defaultOrderBy, defaultOrderDir } = options;
    orderByParamName = orderByParamName ?? "orderBy";
    orderDirParamName = orderDirParamName ?? "orderDir";
    defaultOrderBy = defaultOrderBy ?? WorkAgreementOrderField.Start;
    defaultOrderDir = defaultOrderDir ?? OrderDirection.Descending;
    const orderByParam = searchParams.get(orderByParamName)?.toLowerCase();
    const orderDirParam = searchParams.get(orderDirParamName)?.toLowerCase();

    const orderBy =
      Object.values(WorkAgreementOrderField).find((field) => field.toLowerCase() === orderByParam) ?? defaultOrderBy;

    const orderDirection =
      Object.values(OrderDirection).find((field) => field.toLowerCase() === orderDirParam) ?? defaultOrderDir;

    return new WorkAgreementOrderParameters({ orderBy, orderDirection });
  }

  public orderDirection: OrderDirection;
  public orderBy: WorkAgreementOrderField;

  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    params.append("orderBy", this.orderBy);
    params.append("orderDir", this.orderDirection);
    return params;
  }

  public constructor(params: { orderBy: WorkAgreementOrderField; orderDirection: OrderDirection }) {
    this.orderBy = params.orderBy;
    this.orderDirection = params.orderDirection;
  }

  public isEqualTo(other: WorkAgreementOrderParameters): boolean {
    if (!other) return false;
    return this.orderBy === other.orderBy && this.orderDirection === other.orderDirection;
  }

  public toJSON(): WorkAgreementOrderParametersParams {
    return {
      orderBy: this.orderBy,
      orderDirection: this.orderDirection,
    };
  }
}
